<template>
  <!-- Empty dashboard -->
  <div v-if="!tiles.length" class="grid-empty">
    <GoblinIcon variant="empty" :size="64" />
    <p class="empty-title">No tiles yet</p>
    <p class="empty-sub">
      {{
        editing
          ? 'Use “Add tile” to put a metric chart or a logs list on this dashboard.'
          : 'This dashboard is empty.'
      }}
    </p>
  </div>

  <!-- The grid: 12 columns on desktop, ONE full-width column on mobile.
       Tiles stack in array order (up/down reorder + S/M/L sizes — no 2D drag). -->
  <div v-else class="dash-grid">
    <article
      v-for="(tile, i) in tiles"
      :key="tile.id"
      class="dash-tile"
      :style="{ gridColumn: `span ${Math.min(Math.max(tile.w, 1), GRID_COLS)}` }"
    >
      <header class="tile-head">
        <h3 class="tile-title" :title="tileTitle(tile)">{{ tileTitle(tile) }}</h3>
        <div v-if="editing" class="tile-controls">
          <LgIconButton
            label="Move up"
            size="sm"
            :disabled="i === 0"
            @click="$emit('move', tile.id, 'up')"
          >
            <ChevronUp :size="14" aria-hidden="true" />
          </LgIconButton>
          <LgIconButton
            label="Move down"
            size="sm"
            :disabled="i === tiles.length - 1"
            @click="$emit('move', tile.id, 'down')"
          >
            <ChevronDown :size="14" aria-hidden="true" />
          </LgIconButton>
          <div class="size-step" role="group" aria-label="Tile size">
            <button
              v-for="s in SIZE_STEPS"
              :key="s"
              type="button"
              class="size-btn"
              :class="{ active: sizeOf(tile) === s }"
              :aria-pressed="sizeOf(tile) === s"
              :title="SIZE_LABEL[s]"
              @click="$emit('resize', tile.id, s)"
            >
              {{ s }}
            </button>
          </div>
          <LgIconButton label="Remove tile" size="sm" @click="$emit('remove', tile.id)">
            <Trash2 :size="14" aria-hidden="true" />
          </LgIconButton>
        </div>
      </header>

      <MetricTile
        v-if="tile.type === 'metric'"
        :config="tile.config as ResolvedMetricTileConfig"
        :from="from"
        :to="to"
        :height="contentHeight(tile)"
        @unauthorized="$emit('unauthorized')"
      />
      <LogsTile
        v-else
        :config="tile.config as ResolvedLogsTileConfig"
        :from="from"
        :to="to"
        :live-capped="live && cappedIds.has(tile.id)"
        :height="contentHeight(tile)"
        @unauthorized="$emit('unauthorized')"
      />
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-vue-next';
import type { ResolvedLogsTileConfig, ResolvedMetricTileConfig, ResolvedTile } from '../../api.js';
import {
  GRID_COLS,
  SIZE_STEPS,
  SIZE_LABEL,
  TILE_ROW_PX,
  sizeOf,
  tileTitle,
  cappedLogsTileIds,
  type SizeStep,
} from '../../lib/dashboard-layout.js';
import MetricTile from './MetricTile.vue';
import LogsTile from './LogsTile.vue';
import LgIconButton from '../../ui/LgIconButton.vue';
import GoblinIcon from '../GoblinIcon.vue';

const props = withDefaults(
  defineProps<{
    tiles: ResolvedTile[];
    /** The dashboard clock's window (§3.4) — one window for every tile. */
    from: number;
    to: number;
    /** Whether the clock is live (drives the log-tile live cap note, §5). */
    live?: boolean;
    /** Edit mode: shows reorder / size / remove controls on each tile. */
    editing?: boolean;
  }>(),
  { live: false, editing: false },
);

defineEmits<{
  move: [id: string, dir: 'up' | 'down'];
  resize: [id: string, size: SizeStep];
  remove: [id: string];
  unauthorized: [];
}>();

const cappedIds = computed(() => cappedLogsTileIds(props.tiles));

function contentHeight(tile: ResolvedTile): number {
  return Math.max(tile.h, 1) * TILE_ROW_PX;
}
</script>

<style scoped>
.dash-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--lg-space-3);
  align-items: start;
}
.dash-tile {
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-lg);
  background: var(--lg-bg-surface);
}
.tile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border-faint);
  flex-shrink: 0;
  min-width: 0;
}
.tile-title {
  margin: 0;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  color: var(--lg-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.tile-controls {
  display: flex;
  align-items: center;
  gap: var(--lg-space-1);
  flex-shrink: 0;
}
.size-step {
  display: inline-flex;
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-sm);
  overflow: hidden;
}
.size-btn {
  font: inherit;
  font-size: var(--lg-fs-xs);
  font-family: var(--lg-font-mono);
  width: 24px;
  height: 24px;
  padding: 0;
  background: var(--lg-bg-surface);
  color: var(--lg-text-dim);
  border: none;
  border-right: 1px solid var(--lg-border);
  cursor: pointer;
}
.size-btn:last-child {
  border-right: none;
}
.size-btn:hover {
  color: var(--lg-text);
}
.size-btn.active {
  background: var(--lg-accent);
  color: var(--lg-accent-fg);
  font-weight: 600;
}
.size-btn:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.grid-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-8) var(--lg-space-4);
  color: var(--lg-text-faint);
  text-align: center;
}
.empty-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.empty-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 46ch;
}

/* Mobile (390px target): a single full-width column — every tile spans the
   whole row regardless of its stored w (amendments §0), and reorder still
   applies since tiles stack in array order. `!important` beats the inline
   `grid-column: span N`. */
@media (max-width: 768px) {
  .dash-grid {
    grid-template-columns: 1fr;
    gap: var(--lg-space-2);
  }
  .dash-tile {
    grid-column: 1 / -1 !important;
  }
}
</style>
